import { create } from 'zustand';
import { emitConnectorSyncStarted } from '@/plugins/connector-hub/connector-events';

// ─── DTOs & Request Types ──────────────────────────────────────────────────────

export interface ConnectorDto {
  id: string;
  workspaceId: string;
  organizationId: string;
  /** 0 = SharePoint, 1 = Filesystem, 2 = ERP */
  type: number;
  name: string;
  configJson: string;
  enabled: boolean;
  lastSyncAt: string | null;
  lastSyncStatus: string | null;
  lastSyncError: string | null;
  syncIntervalMinutes: number;
  createdAt: string;
  updatedAt: string;
  scheduleMode?: string;
  scheduledTimesJson?: string | null;
  notificationsEnabled?: boolean;
}

export interface AgentDto {
  id: string;
  name: string;
  hostname: string | null;
  version: string | null;
  isOnline: boolean;
  lastHeartbeat: string;
  pendingChanges: number;
}

export interface ConnectorScheduleDto {
  scheduleMode: string;           // "interval" | "scheduled" | "realtime"
  scheduledTimesJson: string | null;
  notificationsEnabled: boolean;
  syncIntervalMinutes: number;
}

/** Normalize connector type: API may return string enum ('Filesystem') or integer (1). */
const CONNECTOR_TYPE_MAP: Record<string, number> = {
  SharePoint: 0,
  Filesystem: 1,
  ERP: 2,
};

function normalizeConnector(c: ConnectorDto): ConnectorDto {
  if (typeof c.type === 'string') {
    return { ...c, type: CONNECTOR_TYPE_MAP[c.type as string] ?? (c.type as unknown as number) };
  }
  return c;
}

export interface CreateConnectorRequest {
  type: number;
  name: string;
  configJson: string;
  enabled: boolean;
  syncIntervalMinutes: number;
}

export interface UpdateConnectorRequest {
  name?: string;
  configJson?: string;
  enabled?: boolean;
  syncIntervalMinutes?: number;
}

// ─── Store State & Actions ─────────────────────────────────────────────────────

interface ConnectorState {
  connectors: ConnectorDto[];
  loading: boolean;
  error: string | null;
  /** Connector IDs that are currently syncing */
  syncingIds: Set<string>;
  /** Connector IDs for which a cancel request is in flight */
  cancellingIds: Set<string>;
  /** Schedule data keyed by connector ID */
  schedules: Record<string, ConnectorScheduleDto>;
  /** Agents keyed by connector ID */
  agents: Record<string, AgentDto[]>;
  agentLoading: boolean;

  // Actions
  fetchConnectors: (workspaceId: string, token?: string | null) => Promise<void>;
  createConnector: (
    workspaceId: string,
    data: CreateConnectorRequest,
    token?: string | null,
  ) => Promise<ConnectorDto | null>;
  updateConnector: (
    workspaceId: string,
    id: string,
    data: UpdateConnectorRequest,
    token?: string | null,
  ) => Promise<boolean>;
  deleteConnector: (
    workspaceId: string,
    id: string,
    token?: string | null,
  ) => Promise<boolean>;
  triggerSync: (
    workspaceId: string,
    id: string,
    token?: string | null,
  ) => Promise<boolean>;
  triggerDryRun: (
    workspaceId: string,
    id: string,
    token?: string | null,
  ) => Promise<unknown>;
  testConnection: (
    workspaceId: string,
    id: string,
    token?: string | null,
  ) => Promise<{ success: boolean; message?: string }>;
  setSyncing: (id: string, value: boolean) => void;
  cancelSync: (connectorId: string, workspaceId: string, token?: string | null) => Promise<void>;
  fetchSchedule: (connectorId: string, workspaceId: string, token?: string | null) => Promise<void>;
  updateSchedule: (connectorId: string, workspaceId: string, data: ConnectorScheduleDto, token?: string | null) => Promise<void>;
  fetchAgents: (workspaceId: string, connectorId: string, token?: string | null) => Promise<void>;
  registerAgent: (workspaceId: string, connectorId: string, name: string, token?: string | null) => Promise<{ agentId: string; agentKey: string }>;
  deregisterAgent: (workspaceId: string, connectorId: string, agentId: string, token?: string | null) => Promise<void>;
  pingAgent: (workspaceId: string, connectorId: string, agentId: string, token?: string | null) => Promise<{ online: boolean; lastHeartbeat: string }>;
}

// ─── Helper ───────────────────────────────────────────────────────────────────

function buildHeaders(token?: string | null): HeadersInit {
  const headers: HeadersInit = { 'Content-Type': 'application/json' };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  return headers;
}

// ─── Store ────────────────────────────────────────────────────────────────────

export const useConnectorStore = create<ConnectorState>((set, get) => ({
  connectors: [],
  loading: false,
  error: null,
  syncingIds: new Set<string>(),
  cancellingIds: new Set<string>(),
  schedules: {},
  agents: {},
  agentLoading: false,

  /**
   * Fetch all connectors for a workspace.
   */
  fetchConnectors: async (workspaceId, token) => {
    set({ loading: true, error: null });
    try {
      const res = await fetch(
        `/api/dotnet/workspaces/${workspaceId}/connectors`,
        { headers: buildHeaders(token) },
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = (await res.json()) as ConnectorDto[];
      set({ connectors: Array.isArray(data) ? data.map(normalizeConnector) : [], loading: false });
    } catch (e) {
      set({ error: String(e), loading: false });
    }
  },

  /**
   * Create a new connector.
   */
  createConnector: async (workspaceId, data, token) => {
    set({ loading: true, error: null });
    try {
      const res = await fetch(
        `/api/dotnet/workspaces/${workspaceId}/connectors`,
        {
          method: 'POST',
          headers: buildHeaders(token),
          body: JSON.stringify(data),
        },
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const created = normalizeConnector((await res.json()) as ConnectorDto);
      set((state) => ({
        connectors: [...state.connectors, created],
        loading: false,
      }));
      return created;
    } catch (e) {
      set({ error: String(e), loading: false });
      return null;
    }
  },

  /**
   * Update an existing connector.
   */
  updateConnector: async (workspaceId, id, data, token) => {
    set({ error: null });
    try {
      const res = await fetch(
        `/api/dotnet/workspaces/${workspaceId}/connectors/${id}`,
        {
          method: 'PUT',
          headers: buildHeaders(token),
          body: JSON.stringify(data),
        },
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const updated = normalizeConnector((await res.json()) as ConnectorDto);
      set((state) => ({
        connectors: state.connectors.map((c) => (c.id === id ? updated : c)),
      }));
      return true;
    } catch (e) {
      set({ error: String(e) });
      return false;
    }
  },

  /**
   * Delete a connector.
   */
  deleteConnector: async (workspaceId, id, token) => {
    set({ error: null });
    try {
      const res = await fetch(
        `/api/dotnet/workspaces/${workspaceId}/connectors/${id}`,
        {
          method: 'DELETE',
          headers: buildHeaders(token),
        },
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      set((state) => ({
        connectors: state.connectors.filter((c) => c.id !== id),
      }));
      return true;
    } catch (e) {
      set({ error: String(e) });
      return false;
    }
  },

  /**
   * Trigger a manual sync for a connector.
   * Emits `connector:sync-started` on the global EventBus.
   */
  triggerSync: async (workspaceId, id, token) => {
    set({ error: null });
    try {
      const connector = get().connectors.find((c) => c.id === id);
      const res = await fetch(
        `/api/dotnet/workspaces/${workspaceId}/connectors/${id}/sync`,
        {
          method: 'POST',
          headers: buildHeaders(token),
        },
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      get().setSyncing(id, true);

      if (connector) {
        emitConnectorSyncStarted(id, String(connector.type));
      }

      return true;
    } catch (e) {
      set({ error: String(e) });
      return false;
    }
  },

  /**
   * Cancel an in-progress sync for a connector.
   *
   * cancellingIds is cleared either by:
   * a) setSyncing(id, false) when SignalR ConnectorSyncCompleted arrives, or
   * b) immediately if the connector is not actually syncing (no active sync → no completion event)
   * c) safety timeout after 15s as last-resort fallback
   */
  cancelSync: async (connectorId, workspaceId, token) => {
    set((s) => ({ cancellingIds: new Set([...s.cancellingIds, connectorId]) }));

    const clearCancellingId = () => {
      set((s) => {
        const next = new Set(s.cancellingIds);
        next.delete(connectorId);
        return { cancellingIds: next };
      });
    };

    // Safety timeout — clears state if SignalR event never arrives
    const safetyTimer = setTimeout(clearCancellingId, 15_000);

    try {
      await fetch(
        `/api/dotnet/workspaces/${workspaceId}/connectors/${connectorId}/sync/cancel`,
        { method: 'POST', headers: buildHeaders(token) }
      );
    } catch (err) {
      set({ error: String(err) });
    } finally {
      // If the connector isn't actually syncing, no SignalR completion event will arrive.
      // Clear immediately so the UI doesn't get stuck in "Wird abgebrochen…" forever.
      if (!get().syncingIds.has(connectorId)) {
        clearTimeout(safetyTimer);
        clearCancellingId();
      }
    }
  },

  /**
   * Trigger a dry-run via the Python API (reverse proxy).
   */
  triggerDryRun: async (_workspaceId, id, token) => {
    set({ error: null });
    try {
      const headers: HeadersInit = { 'Content-Type': 'application/json' };
      if (token) headers.Authorization = `Bearer ${token}`;

      const connector = get().connectors.find((c) => c.id === id);
      const typeMap: Record<number, string> = { 0: 'sharepoint', 1: 'filesystem', 2: 'erp' };
      const connectorType = connector ? (typeMap[connector.type] ?? '') : '';
      let config: Record<string, unknown> = {};
      try { config = JSON.parse(connector?.configJson ?? '{}') as Record<string, unknown>; } catch { /* empty */ }

      const params = new URLSearchParams({ connector_type: connectorType });
      const res = await fetch(`/api/python/connectors/${id}/dry-run?${params}`, {
        method: 'POST',
        headers,
        body: JSON.stringify(config),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return await res.json();
    } catch (e) {
      set({ error: String(e) });
      return null;
    }
  },

  /**
   * Test the connection for a connector.
   */
  testConnection: async (workspaceId, id, token) => {
    set({ error: null });
    try {
      const res = await fetch(
        `/api/dotnet/workspaces/${workspaceId}/connectors/${id}/test`,
        {
          method: 'POST',
          headers: buildHeaders(token),
        },
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const result = (await res.json()) as { success: boolean; message?: string };
      return result;
    } catch (e) {
      return { success: false, message: String(e) };
    }
  },

  /**
   * Mark a connector as syncing or done (for SignalR-driven updates).
   * When sync finishes (value = false), also clears any pending cancel for that connector.
   */
  setSyncing: (id, value) => {
    set((state) => {
      const nextSyncingIds = new Set(state.syncingIds);
      if (value) {
        nextSyncingIds.add(id);
        return { syncingIds: nextSyncingIds };
      } else {
        nextSyncingIds.delete(id);
        const nextCancellingIds = new Set(state.cancellingIds);
        nextCancellingIds.delete(id);
        return { syncingIds: nextSyncingIds, cancellingIds: nextCancellingIds };
      }
    });
  },

  /**
   * Fetch schedule settings for a connector.
   */
  fetchSchedule: async (connectorId, workspaceId, token) => {
    set({ error: null });
    try {
      const res = await fetch(
        `/api/dotnet/workspaces/${workspaceId}/connectors/${connectorId}/schedule`,
        { headers: buildHeaders(token) },
      );
      if (res.ok) {
        const data = (await res.json()) as ConnectorScheduleDto;
        set((s) => ({ schedules: { ...s.schedules, [connectorId]: data } }));
      }
    } catch (e) {
      set({ error: String(e) });
    }
  },

  /**
   * Fetch all agents registered for a connector.
   */
  fetchAgents: async (workspaceId, connectorId, token) => {
    set({ agentLoading: true });
    try {
      const res = await fetch(
        `/api/dotnet/workspaces/${workspaceId}/connectors/${connectorId}/agents`,
        { headers: buildHeaders(token) },
      );
      if (!res.ok) throw new Error(`fetchAgents failed: ${res.status}`);
      const data = (await res.json()) as AgentDto[];
      set((s) => ({ agents: { ...s.agents, [connectorId]: data } }));
    } catch (e) {
      set({ error: String(e) });
    } finally {
      set({ agentLoading: false });
    }
  },

  /**
   * Register a new agent for a connector. Returns the agentId and agentKey.
   */
  registerAgent: async (workspaceId, connectorId, name, token) => {
    const res = await fetch(
      `/api/dotnet/workspaces/${workspaceId}/connectors/${connectorId}/agents`,
      {
        method: 'POST',
        headers: buildHeaders(token),
        body: JSON.stringify({ name }),
      },
    );
    if (!res.ok) throw new Error(`registerAgent failed: ${res.status}`);
    const data = (await res.json()) as { agentId: string; agentKey: string };
    await get().fetchAgents(workspaceId, connectorId, token);
    return data;
  },

  /**
   * Deregister (delete) an agent from a connector.
   */
  deregisterAgent: async (workspaceId, connectorId, agentId, token) => {
    const res = await fetch(
      `/api/dotnet/workspaces/${workspaceId}/connectors/${connectorId}/agents/${agentId}`,
      { method: 'DELETE', headers: buildHeaders(token) },
    );
    if (!res.ok) throw new Error(`deregisterAgent failed: ${res.status}`);
    await get().fetchAgents(workspaceId, connectorId, token);
  },

  /**
   * Ping a specific agent to check its online status.
   */
  pingAgent: async (workspaceId, connectorId, agentId, token) => {
    const res = await fetch(
      `/api/dotnet/workspaces/${workspaceId}/connectors/${connectorId}/agents/${agentId}/ping`,
      { method: 'POST', headers: buildHeaders(token) },
    );
    if (!res.ok) throw new Error(`pingAgent failed: ${res.status}`);
    return res.json() as Promise<{ online: boolean; lastHeartbeat: string }>;
  },

  /**
   * Update schedule settings for a connector and sync to Python scheduler.
   */
  updateSchedule: async (connectorId, workspaceId, data, token) => {
    set({ error: null });
    try {
      const res = await fetch(
        `/api/dotnet/workspaces/${workspaceId}/connectors/${connectorId}/schedule`,
        {
          method: 'PUT',
          headers: buildHeaders(token),
          body: JSON.stringify(data),
        },
      );
      if (res.ok) {
        const updated = (await res.json()) as ConnectorScheduleDto;
        set((s) => ({ schedules: { ...s.schedules, [connectorId]: updated } }));
        // Also sync Python scheduler
        await fetch(
          `/api/python/connectors/${connectorId}/schedule`,
          {
            method: 'PUT',
            headers: buildHeaders(token),
            body: JSON.stringify(data),
          },
        );
      }
    } catch (e) {
      set({ error: String(e) });
    }
  },
}));
